import { motion } from 'framer-motion';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  progress?: number;
  message?: string;
  className?: string;
}

const sizeMap = {
  sm: 24,
  md: 48,
  lg: 72,
};

export function LoadingSpinner({
  size = 'md',
  progress,
  message,
  className = '',
}: LoadingSpinnerProps) {
  const px = sizeMap[size];
  const strokeWidth = size === 'sm' ? 3 : 4;
  const radius = (px - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const showProgress = progress !== undefined && progress >= 0;

  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={message ?? 'Loading'}
    >
      <div className="relative" style={{ width: px, height: px }}>
        {/* Background circle */}
        <svg width={px} height={px} className="absolute inset-0">
          <circle
            cx={px / 2}
            cy={px / 2}
            r={radius}
            fill="none"
            stroke="#E8E5E0"
            strokeWidth={strokeWidth}
          />
        </svg>

        {showProgress ? (
          /* Determinate progress */
          <svg width={px} height={px} className="absolute inset-0 -rotate-90">
            <motion.circle
              cx={px / 2}
              cy={px / 2}
              r={radius}
              fill="none"
              stroke="#6C3CE1"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{
                strokeDashoffset:
                  circumference - (progress / 100) * circumference,
              }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </svg>
        ) : (
          /* Indeterminate spinner */
          <motion.svg
            width={px}
            height={px}
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          >
            <circle
              cx={px / 2}
              cy={px / 2}
              r={radius}
              fill="none"
              stroke="#6C3CE1"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * 0.75}
            />
          </motion.svg>
        )}

        {/* Progress text */}
        {showProgress && size !== 'sm' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-vestir-purple">
              {Math.round(progress)}%
            </span>
          </div>
        )}
      </div>

      {message && (
        <p className="text-sm text-vestir-gray-dark text-center">{message}</p>
      )}
    </div>
  );
}
